import React, { FC } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ReactECharts from 'echarts-for-react';
import { useTheme } from 'styled-components';
import { useTranslate } from '@/translations';
import {
  MaintenanceSeries,
  useMaintenanceSummary,
} from '@/api/OperationsDashboard';
import { ChartCard } from './ChartCard';

const HEIGHT = 360;

// Monday (d MMM) for each of the last N ISO weeks, oldest first.
const weekLabels = (count: number): string[] => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() - 7 * (count - 1 - i));
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  });
};

interface SparklineProps {
  requested: number[];
  performed: number[];
  labels: string[];
  highlight: 'requested' | 'performed';
  requestedName: string;
  performedName: string;
  requestedColor: string;
  performedColor: string;
}

const Sparkline: FC<SparklineProps> = ({
  requested,
  performed,
  labels,
  highlight,
  requestedName,
  performedName,
  requestedColor,
  performedColor,
}) => {
  const line = (
    data: number[],
    name: string,
    color: string,
    visible: boolean,
  ) => ({
    type: 'line' as const,
    name,
    data,
    showSymbol: false,
    symbol: visible ? 'circle' : ('none' as const),
    itemStyle: { color },
    lineStyle: { color, width: 1.5, opacity: visible ? 1 : 0 },
    ...(visible ? { areaStyle: { color, opacity: 0.12 } } : {}),
  });

  const option: echarts.EChartsOption = {
    grid: { left: 2, right: 2, top: 2, bottom: 2 },
    xAxis: { type: 'category', show: false, data: labels },
    yAxis: { type: 'value', show: false },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      formatter: (params) => {
        const arr = Array.isArray(params) ? params : [params];
        const rows = arr
          .map((p) => `${p.marker} ${p.seriesName}: <b>${p.data}</b>`)
          .join('<br/>');
        return `${arr[0]?.axisValue ?? ''}<br/>${rows}`;
      },
    },
    series: [
      line(requested, requestedName, requestedColor, highlight === 'requested'),
      line(performed, performedName, performedColor, highlight === 'performed'),
    ],
  };
  return <ReactECharts option={option} style={{ height: 32, width: 140 }} />;
};

export const MaintenanceTable: FC = () => {
  const { t } = useTranslate();
  const { primaryChartColor, secondaryChartColor, accent } = useTheme();
  const { data, isLoading, isError, refetch } = useMaintenanceSummary();

  const labels = weekLabels(data?.[0]?.requested.length ?? 6);

  const columns: ColumnsType<MaintenanceSeries> = [
    { title: t('dashboard.maintenance.type'), dataIndex: 'type', key: 'type' },
    {
      title: t('dashboard.maintenance.requested'),
      key: 'requested',
      render: (_, row) => (
        <Sparkline
          requested={row.requested}
          performed={row.performed}
          labels={labels}
          highlight="requested"
          requestedName={t('dashboard.maintenance.requestedShort')}
          performedName={t('dashboard.maintenance.performedShort')}
          requestedColor={primaryChartColor}
          performedColor={secondaryChartColor}
        />
      ),
    },
    {
      title: t('dashboard.maintenance.performed'),
      key: 'performed',
      render: (_, row) => (
        <Sparkline
          requested={row.requested}
          performed={row.performed}
          labels={labels}
          highlight="performed"
          requestedName={t('dashboard.maintenance.requestedShort')}
          performedName={t('dashboard.maintenance.performedShort')}
          requestedColor={primaryChartColor}
          performedColor={secondaryChartColor}
        />
      ),
    },
    {
      title: t('dashboard.maintenance.gap'),
      key: 'gap',
      render: (_, row) => {
        const gap = row.performedTotal - row.requestedTotal;
        const pct =
          row.requestedTotal > 0
            ? Math.round((Math.abs(gap) / row.requestedTotal) * 100)
            : 0;
        const color =
          gap < 0 && pct >= 20 ? '#e53935' : gap < 0 ? accent : '#00b087';
        return (
          <span style={{ color }}>
            {gap > 0 ? `+${gap}` : gap} ({pct}%)
          </span>
        );
      },
    },
  ];

  return (
    <ChartCard
      title={t('dashboard.widget.maintenance')}
      isLoading={isLoading}
      isError={isError}
      isEmpty={data?.length === 0}
      onRetry={() => refetch()}
      height={HEIGHT}
    >
      <Table
        rowKey="type"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={data ?? []}
        scroll={{ y: HEIGHT - 60 }}
      />
    </ChartCard>
  );
};
